import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, TextField, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Avatar, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, MenuItem, CircularProgress, Alert,
  IconButton, InputAdornment, Tooltip, Switch
} from '@mui/material';
import { PersonAdd, Search, Close, Edit } from '@mui/icons-material';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

const roles = ['admin','doctor','nurse','receptionist','pharmacist','accountant'];
const roleConfig: Record<string, { bg: string; color: string }> = {
  admin:        { bg: '#dbeafe', color: '#1d4ed8' },
  doctor:       { bg: '#dcfce7', color: '#15803d' },
  nurse:        { bg: '#ede9fe', color: '#6d28d9' },
  receptionist: { bg: '#fce7f3', color: '#be185d' },
  pharmacist:   { bg: '#cffafe', color: '#0e7490' },
  accountant:   { bg: '#ffedd5', color: '#c2410c' },
  superadmin:   { bg: '#fef9c3', color: '#a16207' },
};

const defaultForm = {
  name: '', email: '', password: '', role: 'receptionist',
  phone: '', specialization: '', licenseNumber: ''
};

const Staff = () => {
  const { user } = useAuth();
  const [staff,      setStaff]      = useState<any[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [open,       setOpen]       = useState(false);
  const [form,       setForm]       = useState<any>(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState('');
  const [total,      setTotal]      = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/auth/staff', { params: { search } });
      setStaff(res.data.data || []);
      setTotal(res.data.data?.length || 0);
    } catch (e: any) {
      toast.error('Failed to load staff: ' + (e.response?.data?.message || e.message));
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.password) {
      setError('Name, email and password are required');
      return;
    }
    setError(''); setSubmitting(true);
    try {
      await api.post('/auth/register', {
        ...form,
        hospitalId: user?.hospital?._id
      });
      toast.success('Staff member added successfully');
      setOpen(false);
      setForm(defaultForm);
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add staff member');
    } finally { setSubmitting(false); }
  };

  const toggleActive = async (staffId: string) => {
    try {
      await api.put(`/auth/staff/${staffId}/toggle`);
      toast.success('Status updated');
      load();
    } catch { toast.error('Failed to update status'); }
  };

  const set = (k: string, v: string) => setForm((f: any) => ({ ...f, [k]: v }));

  const roleGroups = roles.reduce((acc: any, r) => {
    acc[r] = staff.filter(s => s.role === r).length;
    return acc;
  }, {});

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>Staff Management</Typography>
          <Typography sx={{ color: '#64748b', fontSize: 14, mt: 0.3 }}>{total} staff members</Typography>
        </Box>
        <Button variant="contained" startIcon={<PersonAdd />}
          onClick={() => { setOpen(true); setError(''); setForm(defaultForm); }}
          sx={{ borderRadius: 2.5, background: 'linear-gradient(135deg,#0d9488,#0f766e)', textTransform: 'none', fontWeight: 600, px: 2.5, boxShadow: '0 4px 14px rgba(13,148,136,0.35)' }}>
          Add Staff
        </Button>
      </Box>

      {/* Role summary chips */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        {Object.entries(roleGroups).filter(([, v]) => (v as number) > 0).map(([role, count]) => {
          const rc = roleConfig[role] || roleConfig.admin;
          return (
            <Chip key={role} label={`${role}: ${count}`} size="small"
              sx={{ bgcolor: rc.bg, color: rc.color, fontWeight: 600, fontSize: 11, textTransform: 'capitalize' }} />
          );
        })}
      </Box>

      {/* Search */}
      <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, p: 2, mb: 2 }}>
        <TextField fullWidth size="small" placeholder="Search staff by name or email..."
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: '#94a3b8', fontSize: 18 }} /></InputAdornment> }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, bgcolor: '#f8fafc' } }} />
      </Card>

      {/* Table */}
      <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: '#0d9488' }} /></Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  {['Staff Member', 'Role', 'Phone', 'Specialization', 'Last Login', 'Status', 'Actions'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, color: '#374151', py: 1.5, fontSize: 12, borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {staff.map(s => {
                  const rc = roleConfig[s.role] || roleConfig.admin;
                  return (
                    <TableRow key={s._id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ width: 36, height: 36, bgcolor: rc.bg, color: rc.color, fontSize: 13, fontWeight: 800 }}>
                            {s.name?.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{s.name}</Typography>
                            <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>{s.email}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={s.role} size="small"
                          sx={{ bgcolor: rc.bg, color: rc.color, fontWeight: 600, fontSize: 11, textTransform: 'capitalize' }} />
                      </TableCell>
                      <TableCell><Typography sx={{ fontSize: 13 }}>{s.phone || '—'}</Typography></TableCell>
                      <TableCell><Typography sx={{ fontSize: 13, color: '#64748b' }}>{s.specialization || '—'}</Typography></TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>
                          {s.lastLogin ? new Date(s.lastLogin).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' }) : 'Never'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={s.isActive ? 'Active' : 'Inactive'} size="small"
                          sx={{ bgcolor: s.isActive ? '#dcfce7' : '#f1f5f9', color: s.isActive ? '#15803d' : '#64748b', fontWeight: 600, fontSize: 11 }} />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={s.isActive ? 'Deactivate' : 'Activate'}>
                          <Switch size="small" checked={s.isActive}
                            onChange={() => toggleActive(s._id)}
                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#0d9488' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#0d9488' } }} />
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {staff.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <Typography sx={{ color: '#94a3b8' }}>
                        {search ? `No staff matching "${search}"` : 'No staff members found. Add your first staff member.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Add Staff Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, borderBottom: '1px solid #f1f5f9' }}>
          Add Staff Member
          <IconButton size="small" onClick={() => setOpen(false)}><Close fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            {[
              { label: 'Full Name *', k: 'name' },
              { label: 'Email *', k: 'email', type: 'email' },
              { label: 'Password *', k: 'password', type: 'password' },
              { label: 'Phone', k: 'phone' },
              { label: 'Specialization', k: 'specialization' },
              { label: 'License Number', k: 'licenseNumber' },
            ].map(({ label, k, type }) => (
              <Box key={k}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748b', mb: 0.5 }}>{label}</Typography>
                <TextField fullWidth size="small" type={type || 'text'} value={form[k]}
                  onChange={e => set(k, e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Box>
            ))}
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748b', mb: 0.5 }}>Role *</Typography>
              <TextField select fullWidth size="small" value={form.role} onChange={e => set('role', e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                {roles.map(r => (
                  <MenuItem key={r} value={r} sx={{ textTransform: 'capitalize' }}>
                    {r.charAt(0).toUpperCase() + r.slice(1)}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #f1f5f9' }}>
          <Button onClick={() => setOpen(false)} sx={{ textTransform: 'none', color: '#64748b' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}
            sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 600, background: 'linear-gradient(135deg,#0d9488,#0f766e)', px: 3 }}>
            {submitting ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Add Staff Member'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Staff;
